import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { Facebook, Instagram, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const Footer = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const year = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error(t('footer.newsletterInvalidEmail') || 'Please enter a valid email address.');
      return;
    }
    setSubscribing(true);
    try {
      const response = await axios.post(`${API_URL}/api/newsletter/subscribe`, { email });
      if (response.data.message === 'already_verified') {
        navigate('/newsletter-confirmation?status=already_verified');
      } else {
        navigate('/newsletter-confirmation?status=pending');
      }
      setEmail('');
    } catch (error) {
      toast.error(t('footer.newsletterError') || 'An error occurred. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-primary-950 text-white border-t border-white/10 pt-16 pb-8">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="space-y-4">
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-block cursor-pointer"
            >
              <img 
                src="/images/logo-wownautic.png" 
                alt="Wow Nautic" 
                className="h-16 w-auto object-contain"
              />
            </button>
            <p className="text-gray-400 text-sm leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex gap-4">
              <a href="https://www.instagram.com/wow_nautic/" target="_blank" rel="noopener noreferrer" className="bg-white/10 p-2 rounded-full hover:bg-accent-500 transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/p/WOW-Nautic-Boat-and-Jet-Ski-Rentals-61576095742020/" target="_blank" rel="noopener noreferrer" className="bg-white/10 p-2 rounded-full hover:bg-accent-500 transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-bold text-lg mb-6">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3 text-gray-400">
              <li><a href="/#offers" className="hover:text-accent-400 transition-colors">{t('footer.offers')}</a></li>
              <li><a href="/#gallery" className="hover:text-accent-400 transition-colors">{t('footer.gallery')}</a></li>
              <li><a href="/contact" className="hover:text-accent-400 transition-colors">{t('footer.contact')}</a></li>
              <li><a href="/#faq" className="hover:text-accent-400 transition-colors">{t('footer.faq')}</a></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-bold text-lg mb-6">{t('footer.legal')}</h4>
            <ul className="space-y-3 text-gray-400">
              <li><a href="/privacy-policy" className="hover:text-accent-400 transition-colors">{t('footer.privacy')}</a></li>
              <li><a href="/cookie-policy" className="hover:text-accent-400 transition-colors">{t('footer.cookiePolicy')}</a></li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-bold text-lg mb-6">{t('footer.newsletter')}</h4>
            <form onSubmit={handleSubscribe} className="flex flex-col gap-3">
              <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.emailPlaceholder')}
                className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-accent-500 transition-colors"
                required
                disabled={subscribing}
              />
              <Button 
                type="submit" 
                className="bg-accent-500 hover:bg-accent-600 text-white font-bold w-full"
                disabled={subscribing}
              >
                {subscribing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('footer.subscribing') || 'Subscribing...'}</>
                ) : (
                  t('footer.subscribe')
                )}
              </Button>
            </form>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 text-center text-gray-500 text-sm">
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
